import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:bg-tertiary-100 disabled:text-tertiary-400 active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Yellow CTA — the "advance to next stage" action across workflow pages.
        // Disabled state adapts to the surrounding surface:
        //  • Default (white/light surface): grey disabled — clear contrast against white
        //  • Inside `.bg-primary-900` (dark header card): light-primary disabled — stays
        //    visible on dark surfaces where grey would disappear into the background
        accent:
          "border border-accent-400 bg-accent-200 text-primary hover:bg-accent-300 hover:text-primary disabled:opacity-100 disabled:border-tertiary-200 disabled:bg-tertiary-100 disabled:text-tertiary-400 [.bg-primary-900_&]:disabled:border-primary-200 [.bg-primary-900_&]:disabled:bg-primary-100 [.bg-primary-900_&]:disabled:text-primary-500",
        // Dark primary CTA — for "navigate / commit" actions inside a page.
        ink: "bg-primary-800 text-white hover:bg-primary-700 disabled:bg-tertiary-200 disabled:text-tertiary-400",
        // Legacy alias kept for older callers; renders the same as `ink`.
        default:
          "bg-primary-800 text-white hover:bg-primary-700 disabled:bg-tertiary-200 disabled:text-tertiary-400",
        primary:
          "bg-gradient-to-r from-primary-400 to-primary-500 hover:from-primary-500 hover:to-primary-600 border border-transparent text-white shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        warning:
          "text-white bg-system-warning hover:bg-system-warningHover shadow-sm",
        outline:
          "border border-tertiary-200 bg-white text-tertiary-800 hover:bg-tertiary-50 hover:text-tertiary-900",
        tertiary:
          "border border-tertiary-200 bg-white text-tertiary-700 shadow-xs hover:bg-tertiary-50",
        ghost: "text-tertiary-700 hover:bg-tertiary-50 hover:text-tertiary-900",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 text-sm [&_svg]:size-4",
        sm: "h-8 px-3 text-sm [&_svg]:size-3.5",
        xs: "h-8 px-2.5 text-xs [&_svg]:size-3.5",
        lg: "h-11 px-6 text-base [&_svg]:size-4",
        icon: "size-8 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface IButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: IButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
